import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';

import { SyncfusionSharedModule } from 'src/app/shared/syncfusion-shared.module';
import { MaterialModule } from '../app.material';

import { ExportExcelDirective } from '../directives/export-excel.directive';
import { AppFloatOnlyDirective } from '../directives/float-only.directive';
import { IntegerOnlyDirective } from '../directives/integer-only.directive';
import { CurrencyFormatPipe } from '../pipes/currency-format.pipe';
import { HumanizePipe } from '../pipes/humanize.pipe';
import { ReplacePipe } from '../pipes/replace.pipe';
import { CashbackAllocationTableComponent } from './cashback-allocation-table/cashback-allocation-table.component';
import { CashbackRedemptionTableComponent } from './cashback-redemption-table/cashback-redemption-table.component';
import { AccountInformationComponent } from './account-information/account-information.component';
import { AccountStatusComponent } from './account-status/account-status.component';
import { AgentApplicationLinkComponent } from './agent-application-link/agent-application-link.component';
import { AgentFeesComponent } from './agent-fees/agent-fees.component';
import { AlertComponent } from './alert/alert.component';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { BusinessFeesComponent } from './business-fees/business-fees.component';
import { CashbackGraphComponent } from './cashback-graph/cashback-graph.component';
import { ContentTransitionComponent } from './content-transition/content-transition.component';
import { DropdownIconComponent } from './dropdown-icon/dropdown-icon.component';
import { EditLinksComponent } from './edit-links/edit-links.component';
import { EditPencilComponent } from './edit-pencil/edit-pencil.component';
import { EftCashInLinkComponent } from './eft-cash-in-link/eft-cash-in-link.component';
import { EnterpriseFeesComponent } from './enterprise-fees/enterprise-fees.component';
import { ErrorModelComponent } from './error-model/error-model.component';
import { ErrorPopupComponent } from './error-popup/error-popup.component';
import { FieldErrorDisplayComponent } from './field-error-display/field-error-display.component';
import { FilterArrowComponent } from './filter-arrow/filter-arrow';
import { FilterComponent } from './filter/filter.component';
import { HeaderComponent } from './header/header.component';
import { LinksComponent } from './links/links.component';
import { LoaderComponent } from './loader/loader.component';
import { ModalComponent } from './modal/modal.component';
import { NavigationComponent } from './navigation/navigation.component';
import { PageLengthDropdownComponent } from './page-length-dropdown/page-length-dropdown';
import { PageNavigationComponent } from './page-navigation/page-navigation';
import { PersonalUserFeesComponent } from './personal-user-fees/personal-user-fees.component';
import { CashbackReportComponent } from './reports/cashback-report/cashback-report.component';
import { SalesReportComponent } from './reports/sales-report/sales-report.component';
import { SafesHeaderComponent } from './safes-header/safes-header.component';
import { SafesLinkComponent } from './safes-link/safes-link.component';
import { SalesDashboardComponent } from './sales-dashboard/sales-dashboard.component';
import { SalesGraphComponent } from './sales-graph/sales-graph.component';
import { SelectBoxComponent } from './select-box/select-box.component';
import { SelectTypeComponent } from './select-type/select-type.component';
import { SettingsLinkComponent } from './settings-link/settings-link.component';
import { SuccessModelComponent } from './success-model/success-model.component';
import { TransactionViewModalComponent } from './transaction-view-modal/transaction-view-modal.component';
import { TransferToBankLinksComponent } from './transfer-to-bank-links/transfer-to-bank-links.component';
import { WalletBalanceComponent } from './wallet-balance/wallet-balance.component';

// Shared user components
import { UserAddressComponent } from './user-address/user-address.component';
import { UserBankDataComponent } from './user-bank-data/user-bank-data.component';
import { UserProfileDataComponent } from './user-profile-data/user-profile-data.component';
import { UserWalletDataComponent } from './user-wallet-data/user-wallet-data.component';

@NgModule({
    declarations: [
        // Shared components used across the application
        AccountInformationComponent,
        AccountStatusComponent,
        CashbackAllocationTableComponent,
        CashbackRedemptionTableComponent,
        AgentApplicationLinkComponent,
        AgentFeesComponent,
        AlertComponent,
        BreadcrumbComponent,
        BusinessFeesComponent,
        CashbackReportComponent,
        CashbackGraphComponent,
        ContentTransitionComponent,
        DropdownIconComponent,
        EditLinksComponent,
        EditPencilComponent,
        EftCashInLinkComponent,
        EnterpriseFeesComponent,
        ErrorModelComponent,
        ErrorPopupComponent,
        FieldErrorDisplayComponent,
        FilterComponent,
        FilterArrowComponent,
        HeaderComponent,
        LinksComponent,
        LoaderComponent,
        ModalComponent,
        NavigationComponent,
        PageNavigationComponent,
        PageLengthDropdownComponent,
        PersonalUserFeesComponent,
        SalesDashboardComponent,
        SalesGraphComponent,
        SafesHeaderComponent,
        SafesLinkComponent,
        SalesReportComponent,
        SelectBoxComponent,
        SelectTypeComponent,
        SettingsLinkComponent,
        SuccessModelComponent,
        TransactionViewModalComponent,
        TransferToBankLinksComponent,
        WalletBalanceComponent,

        // Shared user components
        UserAddressComponent,
        UserBankDataComponent,
        UserProfileDataComponent,
        UserWalletDataComponent,

        // any shared pipes/directives
        AppFloatOnlyDirective,
        ExportExcelDirective,
        IntegerOnlyDirective,
        CurrencyFormatPipe,
        HumanizePipe,
        ReplacePipe,
    ],
    imports: [
        CommonModule,
        FormsModule,
        MaterialModule,
        NgSelectModule,
        ReactiveFormsModule,
        RouterModule,
        SyncfusionSharedModule,
    ],
    exports: [
        // Common components used across the application
        AccountInformationComponent,
        AccountStatusComponent,
        CashbackAllocationTableComponent,
        CashbackRedemptionTableComponent,
        AgentApplicationLinkComponent,
        AgentFeesComponent,
        AlertComponent,
        BreadcrumbComponent,
        BusinessFeesComponent,
        CashbackGraphComponent,
        CashbackReportComponent,
        ContentTransitionComponent,
        DropdownIconComponent,
        EditLinksComponent,
        EditPencilComponent,
        EftCashInLinkComponent,
        EnterpriseFeesComponent,
        ErrorModelComponent,
        ErrorPopupComponent,
        FieldErrorDisplayComponent,
        FilterComponent,
        FilterArrowComponent,
        HeaderComponent,
        LinksComponent,
        LoaderComponent,
        ModalComponent,
        NavigationComponent,
        PageNavigationComponent,
        PageLengthDropdownComponent,
        PersonalUserFeesComponent,
        SalesDashboardComponent,
        SalesGraphComponent,
        SafesHeaderComponent,
        SafesLinkComponent,
        SalesReportComponent,
        SelectBoxComponent,
        SelectTypeComponent,
        SettingsLinkComponent,
        SuccessModelComponent,
        TransactionViewModalComponent,
        TransferToBankLinksComponent,
        WalletBalanceComponent,

        // Shared user components
        UserAddressComponent,
        UserBankDataComponent,
        UserProfileDataComponent,
        UserWalletDataComponent,

        // export pipes/directives as needed
        AppFloatOnlyDirective,
        ExportExcelDirective,
        IntegerOnlyDirective,
        CurrencyFormatPipe,
        HumanizePipe,
        ReplacePipe,

        RouterModule,
    ],
    providers: [
        HumanizePipe,
    ],
})
export class SharedModule { }
